/**
 * Infer QuickBooks bill link state on a PO from synced qbExpenses (hand-made bills).
 */

import { qboVendorBillAppUrl } from './purchaseQbBillStatus';
import {
  joinProjectPlExpensesToPos,
  type ProjectPlMatchExpense,
  type ProjectPlMatchPo,
  type ProjectPlPoQbJoin,
} from './projectPlPoQbMatch';
import type { PurchaseQbBillStatus } from '../types/purchasing';

export type PurchaseQbBillExpenseLite = ProjectPlMatchExpense;

export type ResolvedPurchaseQbBill = {
  qbBillStatus: PurchaseQbBillStatus;
  qbBillId?: string;
  qbBillUrl?: string;
  qbBillMessage?: string;
  inferredFromQbExpenses: boolean;
};

function toMatchPo(po: ProjectPlMatchPo): ProjectPlMatchPo {
  return po;
}

function joinForPo(
  po: ProjectPlMatchPo,
  expenses: PurchaseQbBillExpenseLite[],
): ProjectPlPoQbJoin {
  return joinProjectPlExpensesToPos(expenses, [toMatchPo(po)]);
}

/** Effective QB bill fields for Purchasing / banners (stored PO stamps + qbExpenses). */
export function resolvePurchaseQbBillFields(
  po: ProjectPlMatchPo,
  expenses: PurchaseQbBillExpenseLite[],
): ResolvedPurchaseQbBill | null {
  const storedStatus = (po.qbBillStatus || '').trim();
  const storedId = (po.qbBillId || '').trim();
  if (storedStatus === 'linked' && storedId) {
    return {
      qbBillStatus: 'linked',
      qbBillId: storedId,
      qbBillUrl: qboVendorBillAppUrl(storedId),
      inferredFromQbExpenses: false,
    };
  }

  const join = joinForPo(po, expenses);
  const match = join.poById[po.id];
  if (match && match.expenseIds.length > 0) {
    const billExpense = match.expenseIds
      .map(id => expenses.find(row => row.id === id))
      .find(row => row && (row.entityType || 'bill') === 'bill' && (row.quickbooksId || '').trim());
    const qbId = (billExpense?.quickbooksId || storedId || '').trim();
    if (match.source === 'bill' && qbId) {
      return {
        qbBillStatus: 'linked',
        qbBillId: qbId,
        qbBillUrl: qboVendorBillAppUrl(qbId),
        qbBillMessage: 'Detected from synced QuickBooks bill in qbExpenses.',
        inferredFromQbExpenses: true,
      };
    }
    if (match.source === 'expense' || match.source === 'heuristic') {
      return {
        qbBillStatus: 'linked',
        qbBillMessage: 'Matched to a QuickBooks charge in qbExpenses.',
        inferredFromQbExpenses: true,
      };
    }
  }

  if (storedStatus && storedStatus !== 'skipped') {
    return {
      qbBillStatus: storedStatus as PurchaseQbBillStatus,
      qbBillId: storedId || undefined,
      qbBillUrl: storedId ? qboVendorBillAppUrl(storedId) : undefined,
      inferredFromQbExpenses: false,
    };
  }

  return null;
}
